'use client'

import { AppsHomeLink } from '@korvo/core/components/shared/AppsHomeLink'
import { DarkModeSwitch } from '@korvo/core/components/shared/DarkModeSwitch'
import { ModuleIcon } from '@korvo/core/components/shared/Icon/ModuleIcon'
import { DrawerExtraContext } from '@korvo/core/components/shared/TopBar/DrawerExtraContext'
import { SideMenu } from '@korvo/core/components/shared/TopBar/SideMenu'
import { UserProfileMenu } from '@korvo/core/components/shared/TopBar/UserProfileMenu'
import { UserAvatarBubble } from '@korvo/core/components/shared/UserAvatarBubble'
import { getSessionUserRole } from '@korvo/core/derivative-app/auth-helpers'
import { moduleService } from '@korvo/core/services/module-service'
import type { Session } from '@korvo/core/types/session'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import type { ShellConfig, TopBarItem } from '../../config/shell-config'
import { useTopBarCenterContent } from '../TopBarCenterContentContext'

type CurrentUserProfile = {
  id: string
  name: string
  email: string
  displayName?: string
  avatarUrl: string | null
  role: string
}

type DefaultTopBarProps = {
  initialSession?: Session | null
  config?: ShellConfig
}

function filterAndSortItems(items: TopBarItem[] | undefined, userRole: string): TopBarItem[] {
  if (!items?.length) return []
  const filtered = items.filter(
    (item) => !item.roles?.length || (userRole && item.roles.includes(userRole))
  )
  return [...filtered].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

export function DefaultTopBar({ initialSession, config }: DefaultTopBarProps) {
  const router = useRouter()
  const pathname = usePathname()
  // Use initialSession from server-side fetch - no client-side useSession hook needed
  // This avoids SSR compilation errors with Better Auth's useSession hook
  const [session, setSession] = useState<Session | null | undefined>(initialSession)
  const [isPending, setIsPending] = useState(!initialSession)
  const [profileData, setProfileData] = useState<CurrentUserProfile | null>(null)
  
  // If no initial session provided, fetch on client after mount
  useEffect(() => {
    if (initialSession !== undefined) return

    let cancelled = false
    const loadSession = async () => {
      try {
        const { authClient } = await import('@/lib/better-auth/client')
        const result = await authClient.getSession()
        if (!cancelled) {
          setSession(result.data as unknown as Session | null)
        }
      } catch {
        if (!cancelled) {
          setSession(null)
        }
      } finally {
        if (!cancelled) {
          setIsPending(false)
        }
      }
    }
    void loadSession()
    return () => {
      cancelled = true
    }
  }, [initialSession])
  
  // Fetch user profile data on client after mount (if authenticated)
  useEffect(() => {
    if (!session?.user?.id) return
    
    const fetchProfile = async () => {
      try {
        const response = await fetch('/api/profile')
        if (response.ok) {
          const data = await response.json()
          setProfileData(data)
        }
      } catch {
        // Profile fetch failed, use session data
      }
    }
    void fetchProfile()
  }, [session?.user?.id])
  
  // Derive currentUserProfile from session and profileData
  const currentUserProfile = useMemo<CurrentUserProfile | null>(() => {
    if (!session?.user) return null
    
    const baseName =
      session.user.name !== undefined && session.user.name !== null ? session.user.name : ''
    const baseEmail =
      session.user.email !== undefined && session.user.email !== null ? session.user.email : ''
    const baseRole = getSessionUserRole(session as unknown as Parameters<typeof getSessionUserRole>[0]) ?? 'USER'
    const sessionUserId = (session.user as { id?: string }).id
    if (!sessionUserId) {
      throw new Error('[DefaultTopBar] session.user.id is missing — auth session is malformed')
    }
    return {
      id: profileData?.id ?? sessionUserId,
      name: profileData?.name ?? baseName ?? baseEmail,
      email: profileData?.email ?? baseEmail,
      displayName: profileData?.displayName ?? baseName ?? undefined,
      avatarUrl: profileData?.avatarUrl ?? session.user.image ?? null,
      role: baseRole,
    }
  }, [session, profileData])
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false)
  const { extraContent, setCloseDrawer } = useContext(DrawerExtraContext)
  useEffect(() => {
    setCloseDrawer(() => () => setIsSideMenuOpen(false))
  }, [setCloseDrawer])
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Match ShellBody MOBILE_BREAKPOINT — useLayoutEffect so /chat mobile topbar appears before paint (Docker Playwright).
  useLayoutEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const isAuthenticatedUser =
    !isPending &&
    !!session?.user &&
    (!!session.user.email || !!(session.user as { id?: string }).id)

  const handleLogout = useCallback(() => {
    const performLogout = async () => {
      // Dynamically import signOut to avoid SSR compilation issues
      const { signOut } = await import('@/lib/better-auth/client')
      await signOut()
      setIsProfileMenuOpen(false)
      setIsSideMenuOpen(false)
      router.push('/')
    }
    void performLogout()
  }, [router])

  const moduleMenuItems = moduleService.getModuleMenuItems()
  const currentModule = moduleService.getCurrentModule(pathname !== null && pathname !== undefined ? pathname : '')

  const userProfile = {
    name:
      currentUserProfile?.displayName ||
      currentUserProfile?.name ||
      session?.user?.name ||
      session?.user?.email ||
      'Guest',
    email: currentUserProfile?.email !== null && currentUserProfile?.email !== undefined && currentUserProfile?.email !== '' ? currentUserProfile.email : (session?.user?.email !== null && session?.user?.email !== undefined && session?.user?.email !== '' ? session.user.email : ''),
    avatar: (currentUserProfile?.avatarUrl ?? session?.user?.image ?? null) ?? undefined,
    role:
      (currentUserProfile?.role as string | undefined) ||
      (session ? getSessionUserRole(session as unknown as Parameters<typeof getSessionUserRole>[0]) : undefined) ||
      'USER',
  }

  const topBarConfig = config?.topBar
  const isMinimalTopBar = topBarConfig?.minimal === true
  const pathPrefixesWithoutSideMenu = topBarConfig?.pathPrefixesWithoutSideMenu
  const pathPrefixesWithSideMenu = topBarConfig?.pathPrefixesWithSideMenu
  const pathPrefixesWithSideMenuOnlyWhenMobile = topBarConfig?.pathPrefixesWithSideMenuOnlyWhenMobile
  const hideLeftNav = useMemo(
    () =>
      pathPrefixesWithoutSideMenu?.some((prefix) => pathname?.startsWith(prefix)) ?? false,
    [pathPrefixesWithoutSideMenu, pathname]
  )
  const showSideMenu = useMemo(() => {
    if (topBarConfig?.showSideMenu === false) return false
    let base: boolean
    if (pathPrefixesWithSideMenu != null && pathPrefixesWithSideMenu.length > 0) {
      base = pathPrefixesWithSideMenu.some((prefix) => pathname?.startsWith(prefix)) ?? false
    } else {
      base = !hideLeftNav
    }
    if (base && pathPrefixesWithSideMenuOnlyWhenMobile?.some((prefix) => pathname?.startsWith(prefix)) && !isMobile) {
      return false
    }
    return base
  }, [topBarConfig?.showSideMenu, pathPrefixesWithSideMenu, pathPrefixesWithSideMenuOnlyWhenMobile, pathname, hideLeftNav, isMobile])
  const showHomeLink = pathname !== '/'
  const showDarkModeToggle = !isMinimalTopBar && topBarConfig?.showDarkModeToggle !== false
  const showUserAvatar = !isMinimalTopBar && topBarConfig?.showUserAvatar !== false
  const leftItems = useMemo(
    () => filterAndSortItems(topBarConfig?.leftItems, userProfile.role),
    [topBarConfig?.leftItems, userProfile.role]
  )
  const rightItems = useMemo(
    () => filterAndSortItems(topBarConfig?.rightItems, userProfile.role),
    [topBarConfig?.rightItems, userProfile.role]
  )
  const CenterContent = topBarConfig?.centerContent
  const { centerContent: dynamicCenterContent } = useTopBarCenterContent()

  return (
    <>
      <header
        className="relative w-full border-b backdrop-blur"
        style={{
          height: 'var(--topbar-height, 3.5rem)',
          minHeight: 'var(--topbar-height, 3.5rem)',
          backgroundColor: 'var(--topbar-background)',
          borderColor: 'var(--topbar-border)',
        }}
      >
        {/* Left: home icon only when sidebar is not shown (when sidebar shown, Home moves into drawer as first item); then config leftItems; Hamburger when path has extra context */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-1 pl-4">
          {showHomeLink && !showSideMenu && (
            <AppsHomeLink
              style={{ color: 'var(--topbar-foreground)', backgroundColor: 'transparent' }}
              className="p-2 justify-center text-sm hover:opacity-90"
              aria-label="Go to home"
            />
          )}
          {leftItems.map((item) =>
            item.render ? (
              <item.render key={item.id} />
            ) : item.href != null ? (
              <Link
                key={item.id}
                href={item.href}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors hover:bg-transparent"
                style={{ color: 'var(--topbar-foreground)', backgroundColor: 'transparent' }}
              >
                {item.icon != null && (
                  <i className={item.icon} aria-hidden style={{ fontSize: '1.25rem' }} />
                )}
                <span>{item.label}</span>
              </Link>
            ) : null
          )}
          {showSideMenu && (
            <button
              onClick={() => setIsSideMenuOpen(!isSideMenuOpen)}
              className="p-2 rounded-md transition-colors hover:bg-transparent"
              style={{
                color: 'var(--topbar-foreground)',
                backgroundColor: 'transparent',
              }}
              aria-label="Open menu"
              data-testid="menu-toggle"
            >
              <ModuleIcon icon="ri-menu-line" className="h-5 w-5" style={{ color: 'var(--topbar-foreground)' }} />
            </button>
          )}
        </div>

        {/* Center: dynamic content (e.g. editor doc name + Saved) or optional config content (hidden in minimal mode) */}
        {!isMinimalTopBar && (dynamicCenterContent != null ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              {dynamicCenterContent}
            </div>
          </div>
        ) : CenterContent != null ? (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
            <div className="pointer-events-auto">
              <CenterContent />
            </div>
          </div>
        ) : null)}

        {/* Right: config rightItems + Theme toggle + Profile */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2 pr-4">
          {rightItems.map((item) =>
            item.render ? (
              <item.render key={item.id} />
            ) : item.href != null ? (
              <Link
                key={item.id}
                href={item.href}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors hover:bg-transparent"
                style={{ color: 'var(--topbar-foreground)', backgroundColor: 'transparent' }}
              >
                {item.icon != null && (
                  <i className={item.icon} aria-hidden style={{ fontSize: '1.25rem' }} />
                )}
                <span>{item.label}</span>
              </Link>
            ) : null
          )}
          {showDarkModeToggle && (
            <DarkModeSwitch
              ariaLabel="Toggle dark mode"
              size="compact"
              transparentHover
              data-testid="topbar-theme-toggle"
            />
          )}
          {/* User Profile - Only show when logged in */}
          {showUserAvatar && isAuthenticatedUser && (
              <div className="relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="p-1 rounded-full transition-colors hover:bg-transparent"
                  style={{ backgroundColor: 'transparent' }}
                  aria-label="User profile"
                  data-testid="topbar-user-menu"
                >
                  <UserAvatarBubble
                    name={userProfile.name}
                    email={userProfile.email}
                    avatarUrl={userProfile.avatar}
                    size={32}
                    data-testid="topbar-user-avatar"
                  />
                </button>
                
                {/* Profile Menu Overlay */}
                {isProfileMenuOpen && (
                  <UserProfileMenu
                    isOpen={isProfileMenuOpen}
                    onClose={() => setIsProfileMenuOpen(false)}
                    userProfile={userProfile}
                    onLogout={handleLogout}
                    isMobile={isMobile}
                  />
                )}
              </div>
            )}
        </div>
      </header>

      {/* Side Menu */}
      {isSideMenuOpen && (
        <SideMenu
          isOpen={isSideMenuOpen}
          onClose={() => setIsSideMenuOpen(false)}
          moduleMenuItems={moduleMenuItems}
          userProfile={userProfile}
          onLogout={handleLogout}
          isMobile={isMobile}
          extraContent={extraContent}
          showDarkModeInDrawer={isMinimalTopBar}
          showHomeAsFirstItem={showHomeLink && showSideMenu}
        />
      )}
    </>
  )
}

